import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import type { Workspace } from '../types';
import { isInstagramConnected } from '../utils/instagram';

type PublishModalProps = {
  workspace: Workspace;
  contentTitle: string;
  onClose: () => void;
  onPublish: () => Promise<void>;
  onMarkManuallyUploaded: (_instagramPostId?: string) => Promise<void>;
  onGoToSettings: () => void;
  loading?: boolean;
};

export function PublishModal({
  workspace,
  contentTitle,
  onClose,
  onPublish,
  onMarkManuallyUploaded,
  onGoToSettings,
  loading = false,
}: PublishModalProps) {
  const [publishing, setPublishing] = useState(false);
  const [markingManual, setMarkingManual] = useState(false);
  const [manualPostId, setManualPostId] = useState('');
  const [error, setError] = useState('');

  const instagramConnected = isInstagramConnected(workspace);

  const handlePublish = async () => {
    setPublishing(true);
    setError('');
    try {
      await onPublish();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to publish content');
      setPublishing(false);
    }
  };

  const handleManualMark = async () => {
    setMarkingManual(true);
    setError('');
    try {
      const normalized = manualPostId.trim();
      await onMarkManuallyUploaded(normalized || undefined);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to mark as manually uploaded');
      setMarkingManual(false);
    }
  };

  if (typeof document === 'undefined') {
    return null;
  }

  if (!instagramConnected) {
    return createPortal(
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Connect Instagram</h2>
            <button className="modal-close" onClick={onClose} disabled={loading}>
              ×
            </button>
          </div>
          <div className="modal-body">
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📱</div>
              <p style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>
                Instagram Account Not Connected
              </p>
              <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
                Please connect your Instagram account in settings to publish content.
              </p>
            </div>
            <div
              style={{
                color: 'var(--text-secondary)',
                fontSize: 14,
                marginBottom: 24,
                padding: '16px',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '8px',
              }}
            >
              <strong>How publishing works</strong>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                <li>Conistra publishes via your stored Instagram session.</li>
                <li>If this says not connected, re-store your session in Settings.</li>
              </ul>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button className="btn btn-success" onClick={onGoToSettings} disabled={loading}>
              Go to Settings
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleManualMark}
              disabled={markingManual || loading}
              title="Use this if you already uploaded the post in Instagram manually"
            >
              {markingManual ? 'Saving…' : 'Mark as Manually Uploaded'}
            </button>
          </div>
          <div
            style={{
              borderTop: '1px solid var(--border-color)',
              marginTop: 12,
              paddingTop: 12,
            }}
          >
            <label
              htmlFor="manual-instagram-post-id-disconnected"
              style={{
                display: 'block',
                fontSize: 12,
                color: 'var(--text-secondary)',
                marginBottom: 6,
              }}
            >
              Optional Instagram Post ID (if you have it)
            </label>
            <input
              id="manual-instagram-post-id-disconnected"
              type="text"
              value={manualPostId}
              onChange={(e) => setManualPostId(e.target.value)}
              placeholder="e.g. 18123456789012345"
              disabled={markingManual || loading}
              style={{
                width: '100%',
                border: '1px solid var(--border-color)',
                borderRadius: 8,
                padding: '10px 12px',
                fontSize: 14,
              }}
            />
            <p
              style={{
                fontSize: 12,
                color: 'var(--text-secondary)',
                marginTop: 6,
                marginBottom: 0,
              }}
            >
              You can mark this as uploaded even without a connected account.
            </p>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Publish to Instagram</h2>
          <button className="modal-close" onClick={onClose} disabled={loading || publishing}>
            ×
          </button>
        </div>
        <div className="modal-body">
          <p style={{ marginBottom: 16, color: 'var(--text-secondary)' }}>
            Ready to publish <strong>"{contentTitle}"</strong> to Instagram?
          </p>
          {error && (
            <div
              style={{
                padding: 12,
                marginBottom: 16,
                backgroundColor: 'rgba(220, 38, 38, 0.1)',
                border: '1px solid rgba(220, 38, 38, 0.3)',
                borderRadius: 8,
                color: '#dc2626',
                fontSize: 14,
              }}
            >
              {error}
            </div>
          )}
          <div
            style={{
              backgroundColor: 'var(--bg-secondary)',
              padding: 16,
              borderRadius: 8,
              marginBottom: 16,
            }}
          >
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
              Instagram Account
            </p>
            <p style={{ fontWeight: 500 }}>
              {workspace.instagrapi_username || workspace.instagram_handle || 'Connected'}
            </p>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={publishing || loading}>
            Cancel
          </button>
          <button
            className="btn btn-success"
            onClick={handlePublish}
            disabled={publishing || markingManual || loading}
          >
            {publishing ? 'Publishing...' : 'Publish Now'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleManualMark}
            disabled={publishing || markingManual || loading}
            title="Use this if you already uploaded the post in Instagram manually"
          >
            {markingManual ? 'Saving…' : 'Mark as Manually Uploaded'}
          </button>
        </div>
        <div
          style={{
            borderTop: '1px solid var(--border-color)',
            marginTop: 12,
            paddingTop: 12,
          }}
        >
          <label
            htmlFor="manual-instagram-post-id"
            style={{
              display: 'block',
              fontSize: 12,
              color: 'var(--text-secondary)',
              marginBottom: 6,
            }}
          >
            Optional Instagram Post ID (if you have it)
          </label>
          <input
            id="manual-instagram-post-id"
            type="text"
            value={manualPostId}
            onChange={(e) => setManualPostId(e.target.value)}
            placeholder="e.g. 18123456789012345"
            disabled={publishing || markingManual || loading}
            style={{
              width: '100%',
              border: '1px solid var(--border-color)',
              borderRadius: 8,
              padding: '10px 12px',
              fontSize: 14,
            }}
          />
          <p
            style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6, marginBottom: 0 }}
          >
            Use this fallback if API publishing fails but you posted manually in Instagram.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}
